import { mdiBank, mdiCashMultiple, mdiSyncAlert } from '@mdi/js';
import {
    AutomationWorkerId,
    CatalogPayer,
    ConditionKind,
    EnterCatalog,
    PayerMatchValue,
    PaymentMatchValue,
    RuleMatch,
    RuleMatchScope,
    RuleMatchValue,
    TransmissionMatchValue,
} from './worker-automation.types';
import { getCatalogPayerName } from '../worker-automation-catalog';
import { formatPayerAdjustmentGroupShortCode, normalizePayerAdjustmentGroup } from '../../dictionary/payer-adjustment-groups';

export const KNOWN_MATCH_SCOPES = ['TRANSMISSION_STATUS', 'PAYMENT', 'PAYER'] as const;

export function isKnownMatchScope(scope: string): scope is (typeof KNOWN_MATCH_SCOPES)[number] {
    return (KNOWN_MATCH_SCOPES as readonly string[]).includes(scope);
}

export function getScopeLabel(scope: RuleMatchScope): string {
    switch (scope) {
        case 'TRANSMISSION_STATUS':
            return 'Transmission status';
        case 'PAYMENT':
            return 'Payment denial codes';
        case 'PAYER':
            return 'Payer';
        default:
            return scope;
    }
}

export function getScopeSelectLabel(scope: RuleMatchScope): string {
    switch (scope) {
        case 'TRANSMISSION_STATUS':
            return 'Transmission status';
        case 'PAYMENT':
            return 'Payment — ERA codes';
        case 'PAYER':
            return 'Payer — insurance company';
        default:
            return scope;
    }
}

export function getScopeIcon(scope: RuleMatchScope): string {
    switch (scope) {
        case 'TRANSMISSION_STATUS':
            return mdiSyncAlert;
        case 'PAYMENT':
            return mdiCashMultiple;
        case 'PAYER':
            return mdiBank;
        default:
            return mdiSyncAlert;
    }
}

export function formatFilterGroupTitle(matchIndex: number): string {
    return `Condition ${matchIndex + 1}`;
}


export function getScopeDescription(scope: RuleMatchScope): string {
    switch (scope) {
        case 'TRANSMISSION_STATUS':
            return 'Filter on clearinghouse / transmission rejection messages.';
        case 'PAYMENT':
            return 'Filter on adjudication results from the ERA (CARC / RARC denial codes).';
        case 'PAYER':
            return 'Filter on the payer / insurance company on the claim. Matches any of the selected payers (OR).';
        default:
            return '';
    }
}

export function getAvailableMatchScopes(workerId: AutomationWorkerId): RuleMatchScope[] {
    if (workerId === 'rejection') {
        return ['TRANSMISSION_STATUS', 'PAYER'];
    }

    return ['PAYMENT', 'PAYER'];
}

export function createEmptyMatchValue(scope: RuleMatchScope): RuleMatchValue {
    if (scope === 'TRANSMISSION_STATUS') {
        return { contains: [] };
    }

    if (scope === 'PAYER') {
        return { payerIds: [] };
    }

    return {
        carc: { group: [], codes: [] },
        rarc: { codes: [] },
    };
}

export function createEmptyMatch(scope: RuleMatchScope): RuleMatch {
    return { scope, value: createEmptyMatchValue(scope) };
}

function normalizePaymentMatchValue(value: RuleMatchValue): PaymentMatchValue {
    const payment = value as Partial<PaymentMatchValue>;

    return {
        carc: {
            group: [...(payment.carc?.group ?? [])],
            codes: [...(payment.carc?.codes ?? [])],
        },
        rarc: {
            codes: [...(payment.rarc?.codes ?? [])],
        },
    };
}

function isPaymentConditionKind(kind: ConditionKind): boolean {
    return kind === 'carcGroup' || kind === 'carcCode' || kind === 'rarcCode';
}

export function getConditionKindsForScope(scope: RuleMatchScope): ConditionKind[] {
    if (scope === 'TRANSMISSION_STATUS') {
        return ['contains'];
    }

    if (scope === 'PAYER') {
        return ['payerId'];
    }

    return ['carcGroup', 'carcCode', 'rarcCode'];
}

export function conditionMetaForScope(
    scope: RuleMatchScope,
    kind: ConditionKind,
): { hint: string; label: string } {
    if (scope === 'TRANSMISSION_STATUS') {
        return { label: 'Transmission status contains', hint: 'match any (OR)' };
    }

    if (scope === 'PAYER') {
        return { label: 'Payer', hint: 'match any (OR)' };
    }

    if (kind === 'carcGroup') {
        return { label: 'CARC group', hint: 'match any (OR)' };
    }
    if (kind === 'carcCode') {
        return { label: 'CARC code', hint: 'match all (AND)' };
    }
    return { label: 'RARC code', hint: 'match all (AND)' };
}

export function cloneMatchValue(value: RuleMatchValue): RuleMatchValue {
    if ('contains' in value) {
        return { contains: [...value.contains] };
    }

    if ('payerIds' in value) {
        return { payerIds: [...value.payerIds] };
    }

    const payment = normalizePaymentMatchValue(value);
    return {
        carc: { group: [...payment.carc.group], codes: [...payment.carc.codes] },
        rarc: { codes: [...payment.rarc.codes] },
    };
}

export function getConditionValues(match: RuleMatch, kind: ConditionKind): string[] {
    if (!getConditionKindsForScope(match.scope).includes(kind)) {
        return [];
    }

    const value = match.value;

    if (kind === 'contains' && 'contains' in value) {
        return value.contains ?? [];
    }

    if (kind === 'payerId' && 'payerIds' in value) {
        return value.payerIds ?? [];
    }

    if (!isPaymentConditionKind(kind)) {
        return [];
    }

    const payment = normalizePaymentMatchValue(value);
    if (kind === 'carcGroup') {
        return payment.carc.group;
    }
    if (kind === 'carcCode') {
        return payment.carc.codes;
    }
    return payment.rarc.codes;
}

export function addConditionValue(
    match: RuleMatch,
    kind: ConditionKind,
    text: string,
): RuleMatch {
    const value = cloneMatchValue(match.value);
    const trimmed = text.trim();
    if (!trimmed) {
        return match;
    }

    if (kind === 'contains' && 'contains' in value) {
        value.contains.push(trimmed);
    } else if (kind === 'payerId' && 'payerIds' in value) {
        value.payerIds.push(trimmed);
    } else if (isPaymentConditionKind(kind)) {
        const payment = normalizePaymentMatchValue(value);
        if (kind === 'carcGroup') {
            payment.carc.group.push(normalizePayerAdjustmentGroup(trimmed));
        } else if (kind === 'carcCode') {
            payment.carc.codes.push(trimmed);
        } else {
            payment.rarc.codes.push(trimmed);
        }
        return { ...match, value: payment };
    }

    return { ...match, value };
}

export function removeConditionValue(
    match: RuleMatch,
    kind: ConditionKind,
    index: number,
): RuleMatch {
    const value = cloneMatchValue(match.value);

    if (kind === 'contains' && 'contains' in value) {
        value.contains.splice(index, 1);
    } else if (kind === 'payerId' && 'payerIds' in value) {
        value.payerIds.splice(index, 1);
    } else if (isPaymentConditionKind(kind)) {
        const payment = normalizePaymentMatchValue(value);
        if (kind === 'carcGroup') {
            payment.carc.group.splice(index, 1);
        } else if (kind === 'carcCode') {
            payment.carc.codes.splice(index, 1);
        } else {
            payment.rarc.codes.splice(index, 1);
        }
        return { ...match, value: payment };
    }

    return { ...match, value };
}

export function formatMatchBlockSummary(
    match: RuleMatch,
    catalog?: EnterCatalog | null,
    payerById?: Record<string, CatalogPayer>,
): string {
    const value = match.value;
    const scopeLabel = getScopeLabel(match.scope);

    if ('contains' in value) {
        return value.contains.length
            ? `${scopeLabel}: ${value.contains.map((item) => `“${item}”`).join(' or ')}`
            : `${scopeLabel}: not set`;
    }

    if ('payerIds' in value) {
        return value.payerIds.length
            ? `${scopeLabel}: ${value.payerIds
                  .map(payerId => getCatalogPayerName(catalog, payerId, payerById) ?? payerId)
                  .join(' or ')}`
            : `${scopeLabel}: not set`;
    }

    const payment = normalizePaymentMatchValue(value);
    const parts: string[] = [];
    if (payment.carc.group.length) {
        parts.push(`CARC ${payment.carc.group.map(formatPayerAdjustmentGroupShortCode).join('/')}`);
    }
    if (payment.carc.codes.length) {
        parts.push(`code ${payment.carc.codes.join(' & ')}`);
    }
    if (payment.rarc.codes.length) {
        parts.push(`RARC ${payment.rarc.codes.join(' & ')}`);
    }

    return parts.length ? `${scopeLabel}: ${parts.join(' · ')}` : `${scopeLabel}: not set`;
}

export function formatRuleMatchSummary(
    matches: RuleMatch[],
    catalog?: EnterCatalog | null,
    payerById?: Record<string, CatalogPayer>,
): string {
    if (!matches.length) {
        return 'No conditions set';
    }

    const summaries = matches
        .map(match => formatMatchBlockSummary(match, catalog, payerById))
        .filter(summary => !summary.endsWith(': not set'));
    return summaries.length ? summaries.join('  ∧  ') : 'No conditions set';
}

export function normalizeRuleMatches(matches: RuleMatch[], fallbackScope: RuleMatchScope): RuleMatch[] {
    if (!matches.length) {
        return [createEmptyMatch(fallbackScope)];
    }

    return matches.map((match) => ({
        scope: match.scope,
        value: cloneMatchValue(match.value),
    }));
}
